import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { fetchCorrections, fetchMyAttendance } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import {
  buildHistory,
  crossesMidnight,
  monthOf,
  monthRange,
  shiftMonth,
} from '../lib/attendance-history';
import { todayInZone } from '../lib/attendance-session';
import { approvedHalfDayLeaveByDate, formatHalfDayPart } from '../lib/correction-form';
import {
  formatDateInZone,
  formatDayStatus,
  formatLateMinutes,
  formatTimeInZone,
} from '../lib/format';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Riwayat absensi per bulan.
 *
 * Menampilkan status yang DIKEMBALIKAN server, tidak pernah menghitung ulang dari jam absen masuk.
 * Perubahan kebijakan hari ini karena itu tidak mengubah tampilan absensi bulan lalu — server
 * memakai `policy_snapshot` yang berlaku saat event terjadi (ADR-0034 B2).
 */
export function HistoryPage(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const [month, setMonth] = useState(() => monthOf(todayInZone(new Date(), timezone)));
  const range = monthRange(month);

  /*
   * `includeEvents: true` — SATU-SATUNYA layar yang memintanya.
   *
   * Tanpa ini `eventsAvailable` selalu palsu, dan tiga hal di bawah tidak pernah muncul: catatan
   * sesi lintas tengah malam, jumlah event mentah, dan peringatan "ada event tanpa verifikasi
   * lokasi penuh" — yang komentarnya sendiri menyatakan WAJIB terlihat. Layarnya tidak pernah
   * rusak; datanya memang tidak pernah dikirim (ditemukan pada sapuan Chrome 22 Agu).
   *
   * Rentangnya satu BULAN, jadi muatan event masih wajar. Layar lain sengaja tidak memintanya.
   */
  const { state, reload } = useAsync(
    () => fetchMyAttendance(session.auth, range.from, range.to, true),
    [range.from, range.to, session.auth],
  );

  /*
   * Keterangan izin setengah hari — MALAS dan SENYAP saat gagal, pola yang sama dengan jatah
   * cuti di `CorrectionNewPage`. Bukan `useAsync`: kegagalan di sini hanya berarti keterangan
   * tidak tampil, dan itu tidak boleh mengubah halaman ini menjadi kartu galat — riwayat hari
   * itu sendiri (dari fetch di atas) tetap sah dan berguna tanpa keterangan ini.
   *
   * Diambil TANPA filter status: MARK_HALF_DAY_LEAVE yang APPROVED tidak pernah tercatat di
   * `AttendanceDay` (lihat komentar enum di server), jadi baris koreksi itu sendiri satu-satunya
   * sumber "sudah disetujui, bagian mana".
   */
  const [setengahHari, setSetengahHari] = useState<ReadonlyMap<string, string> | null>(null);
  useEffect(() => {
    let aktif = true;
    fetchCorrections(session.auth)
      .then((koreksi) => {
        if (aktif) setSetengahHari(approvedHalfDayLeaveByDate(koreksi));
      })
      .catch(() => {
        /* senyap — lihat alasannya di atas */
      });
    return () => {
      aktif = false;
    };
  }, [session.auth]);

  return (
    <>
      <section className="card">
        <h2>Riwayat absensi</h2>
        <div className="row space-between">
          <button type="button" onClick={() => setMonth(shiftMonth(month, -1))}>
            ← Bulan lalu
          </button>
          <strong aria-live="polite">{formatMonthLabel(month)}</strong>
          <button type="button" onClick={() => setMonth(shiftMonth(month, 1))}>
            Bulan depan →
          </button>
        </div>
        <p className="muted small">Waktu ditampilkan dalam zona {timezone}.</p>
      </section>

      {state.status === 'loading' ? <LoadingState label="Memuat riwayat…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready'
        ? (() => {
            const entries = buildHistory(state.data.days, state.data.events);
            const adaEvent = state.data.eventsAvailable;
            if (entries.length === 0) {
              return (
                <EmptyState
                  title="Belum ada catatan bulan ini"
                  hint="Absensi yang Anda lakukan akan muncul di sini. Bila ada hari yang terlewat, ajukan koreksi."
                />
              );
            }
            return (
              <ul className="list">
                {entries.map((entry) => (
                  <li key={entry.workDate} className="card list-item">
                    <div className="row space-between">
                      <Link to={`/attendance/history/${entry.workDate}`}>
                        {formatDateInZone(`${entry.workDate}T00:00:00.000Z`, 'UTC')}
                      </Link>
                      <span className={`badge status-${entry.day?.status ?? 'NONE'}`}>
                        {formatDayStatus(entry.day?.status ?? null)}
                      </span>
                    </div>

                    <p className="muted small">
                      {entry.day?.firstCheckInAt
                        ? `Masuk ${formatTimeInZone(entry.day.firstCheckInAt, timezone)}`
                        : 'Tidak ada absen masuk'}
                      {' · '}
                      {entry.day?.lastCheckOutAt
                        ? `Pulang ${formatTimeInZone(entry.day.lastCheckOutAt, timezone)}`
                        : 'Tidak ada absen pulang'}
                    </p>

                    {entry.day && entry.day.lateMinutes > 0 ? (
                      <p className="small">{formatLateMinutes(entry.day.lateMinutes)}</p>
                    ) : null}

                    {/*
                      Status DI ATAS tetap apa adanya (ketetapan Owner) — ini keterangan di
                      sampingnya, bukan pengganti. "Terlambat 4 jam" tetap benar secara fakta;
                      baris ini menjelaskan MENGAPA tanpa mengubah fakta itu.
                    */}
                    {setengahHari?.has(entry.workDate) ? (
                      <p className="muted small">
                        ℹ Izin setengah hari ({formatHalfDayPart(setengahHari.get(entry.workDate)!)}
                        ) — disetujui
                      </p>
                    ) : null}

                    {adaEvent && crossesMidnight(entry, timezone) ? (
                      <p className="muted small">
                        Sesi melewati tengah malam — tercatat pada hari kerja saat Anda absen masuk.
                      </p>
                    ) : null}

                    {/* Tidak terverifikasi WAJIB terlihat, bukan disembunyikan di detail. */}
                    {adaEvent && entry.hasUnverifiedLocation ? (
                      <p className="warning small">
                        Ada event tanpa verifikasi lokasi penuh. Buka detail untuk melihat mana.
                      </p>
                    ) : null}

                    {/* "0 event mentah" pada respons yang memang tidak membawa event adalah
                        pernyataan yang salah, bukan sekadar kosong. */}
                    <p className="muted small">
                      {adaEvent ? (
                        `${entry.events.length} event mentah`
                      ) : (
                        <Link to={`/attendance/history/${entry.workDate}`}>
                          Buka detail untuk melihat event mentah
                        </Link>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            );
          })()
        : null}
    </>
  );
}

/** `2026-08` → `Agustus 2026`, tanpa melewati konstruktor Date atas string tanggal. */
function formatMonthLabel(month: string): string {
  const [year, monthIndex] = month.split('-');
  const names = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
  ];
  return `${names[Number(monthIndex) - 1] ?? month} ${year}`;
}
